import moment from 'moment';

const VIOLATION_TYPE_LABELS = {
  LATE_ARRIVAL: 'Late Arrival',
  EARLY_EXIT: 'Early Exit'
};

const WARNING_STATUS_CONFIG = {
  '1/2': { label: '1st warning', color: 'warning' },
  '2/2': { label: '2nd warning', color: 'orange' },
  HR_NOTIFIED: { label: 'HR Action Required', color: 'error' },
  LEAVE_APPLIED: { label: 'Leave Applied', color: 'success' }
};

export function violationDateNumToIstStartMs(dateNum) {
  const s = String(dateNum || '');
  if (s.length < 8) return null;

  return moment(s.slice(0, 8), 'YYYYMMDD').utcOffset(330, true).startOf('day').valueOf();
}

export function violationDateNumToIstEndMs(dateNum) {
  const s = String(dateNum || '');
  if (s.length < 8) return null;

  return moment(s.slice(0, 8), 'YYYYMMDD').utcOffset(330, true).endOf('day').valueOf();
}

export function formatViolationDate(dateNum) {
  const s = String(dateNum || '');
  if (s.length < 8) return '—';
  return `${s.slice(6, 8)}/${s.slice(4, 6)}/${s.slice(0, 4)}`;
}

export function formatViolationDateLong(dateNum) {
  const ms = violationDateNumToIstStartMs(dateNum);
  if (!ms) return '—';
  return moment(ms).utcOffset(330).format('DD MMM YYYY');
}

function formatObservantMs(msVal) {
  const n = Number(msVal);
  if (!Number.isFinite(n) || n <= 0) return null;

  const totalMins = Math.floor(n / 60000);
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;

  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  if (m > 0) return `${m}m`;
  return null;
}

function formatViolationClockTime(ts) {
  const n = Number(ts);
  if (!Number.isFinite(n) || n <= 0) return null;
  return moment(n).utcOffset(330).format('hh:mm A');
}

export function getViolationCheckInLabel(violation = {}) {
  const formatted = String(violation?.checkInTimeFormatted || '').trim();
  if (formatted) return formatted;
  return formatViolationClockTime(violation?.checkInTime);
}

export function getViolationCheckOutLabel(violation = {}) {
  const formatted = String(violation?.checkOutTimeFormatted || '').trim();
  if (formatted) return formatted;
  return formatViolationClockTime(violation?.checkOutTime);
}

export function getViolationObservantLabel(violation = {}) {
  const formatted = String(violation?.observantTotalFormatted || '').trim();
  if (formatted) return formatted;
  return formatObservantMs(violation?.observantTotalMs);
}

export function getViolationHrxWorkedLabel(violation = {}) {
  const formatted = String(violation?.hrxWorkedFormatted || '').trim();
  if (formatted) return formatted;
  return formatObservantMs(violation?.hrxWorkedMs);
}

export function getViolationTimeMeta(violation = {}) {
  return {
    checkIn: getViolationCheckInLabel(violation),
    checkOut: getViolationCheckOutLabel(violation),
    observant: getViolationObservantLabel(violation),
    hrxWorked: getViolationHrxWorkedLabel(violation)
  };
}

export function getViolationActionConfig(violation = {}) {
  if (violation?.leaveApplied) {
    return { label: 'Leave Applied', color: 'success' };
  }

  const action = String(violation?.action || '').toLowerCase();
  const count = Number(violation?.monthlyCount) || 0;

  if (action === 'hr_notified' || violation?.hrNotified) {
    return { label: 'HR Notified', color: 'error' };
  }

  if (count === 1) {
    return { label: '1st warning', color: 'warning' };
  }

  if (count === 2) {
    return { label: '2nd warning', color: 'orange' };
  }

  if (action === 'warning') {
    return { label: 'Warning', color: 'warning' };
  }

  return { label: action || '—', color: 'default' };
}

export function getViolationActionShortLabel(violation = {}) {
  if (violation?.leaveApplied) return 'Leave applied';

  const action = String(violation?.action || '').toLowerCase();
  if (action === 'hr_notified' || violation?.hrNotified) return 'HR notified';

  const count = Number(violation?.monthlyCount) || 0;
  if (count === 1) return 'Warning 1';
  if (count === 2) return 'Warning 2';
  if (count > 2) return `${count}th violation`;

  return 'Warning';
}

export function getSortedViolations(row) {
  const list = Array.isArray(row?.violations) ? [...row.violations] : [];
  return list.sort((a, b) => Number(b?.date || 0) - Number(a?.date || 0));
}

export function getViolationWarningNumber(violation = {}) {
  const count = Number(violation?.monthlyCount);
  if (count === 1 || count === 2) return count;

  const status = String(violation?.warningStatus || '');
  if (status === '1/2') return 1;
  if (status === '2/2') return 2;

  const match = status.match(/^(\d+)\s*\/\s*2$/);
  if (match) {
    const parsed = Number(match[1]);
    if (parsed === 1 || parsed === 2) return parsed;
  }

  return null;
}

/** Employee calendar: only 1st and 2nd warning dates per type. */
export function getFirstAndSecondWarningViolations(summary) {
  const sorted = [...getSortedViolations(summary)].sort(
    (a, b) => Number(a?.date || 0) - Number(b?.date || 0)
  );

  const pickByNumber = (num) =>
    sorted.find((violation) => getViolationWarningNumber(violation) === num);

  const first = pickByNumber(1);
  const second = pickByNumber(2);
  const picked = [];

  if (first) {
    picked.push({ ...first, monthlyCount: 1, warningStatus: first.warningStatus || '1/2' });
  }
  if (second && second?.violationId !== first?.violationId && second?.date !== first?.date) {
    picked.push({ ...second, monthlyCount: 2, warningStatus: second.warningStatus || '2/2' });
  }

  if (picked.length >= 2) return picked;

  const usedDates = new Set(picked.map((violation) => violation?.date));
  for (const violation of sorted) {
    if (picked.length >= 2) break;
    if (usedDates.has(violation?.date)) continue;
    const warningNumber = picked.length + 1;
    picked.push({
      ...violation,
      monthlyCount: warningNumber,
      warningStatus: violation.warningStatus || `${warningNumber}/2`
    });
    usedDates.add(violation?.date);
  }

  return picked;
}

export function getViolationSummaries(userRow) {
  if (Array.isArray(userRow?.violationSummaries)) {
    return userRow.violationSummaries;
  }
  if (userRow?.violationType) {
    return [userRow];
  }
  return [];
}

export function filterViolationSummaries(userRow, violationType) {
  const summaries = getViolationSummaries(userRow);
  if (!violationType || violationType === 'all') return summaries;
  return summaries.filter((summary) => summary.violationType === violationType);
}

export function isViolationHrNotified(violation = {}) {
  if (violation?.leaveApplied) return false;
  const action = String(violation?.action || '').toLowerCase();
  return action === 'hr_notified' || Boolean(violation?.hrNotified);
}

export function canApplyLeaveForViolation(summary, violation) {
  if (violation?.leaveApplied) return false;
  const count = Number(violation?.monthlyCount) || 0;
  if (count === 1 || count === 2) return true;
  if (!canShowApplyLeave(summary)) return false;
  return isViolationHrNotified(violation);
}

export function mergeViolationForApplyLeave(userRow, summary, violation) {
  const merged = {
    ...summary,
    userId: userRow?.userId || getViolationUserId(userRow),
    user: userRow?.user
  };

  if (!violation) return merged;

  return {
    ...merged,
    pendingViolationId: violation?.violationId,
    pendingViolationDate: violation?.date,
    date: violation?.date
  };
}

export function getViolationDateNum(row) {
  return row?.pendingViolationDate ?? row?.violationDate ?? row?.date ?? null;
}

export function getViolationTypeLabel(violationType) {
  return VIOLATION_TYPE_LABELS[violationType] || violationType || '—';
}

export function getLeaveTypeName(violationType) {
  return VIOLATION_TYPE_LABELS[violationType] || '';
}

const VIOLATION_APPLY_LEAVE_BASE_NAMES = ['paid leave', 'unpaid leave'];

export function isUnpaidLeaveType(row) {
  return (
    String(row?.name || '')
      .trim()
      .toLowerCase() === 'unpaid leave'
  );
}

export function getViolationLeaveTypeBalance(row) {
  const balance = Number(row?.availableBalance);
  return Number.isFinite(balance) ? balance : 0;
}

/** Days deducted when applying leave from a violation. */
export function getViolationLeaveDeductionDays(row) {
  const name = String(row?.name || '')
    .trim()
    .toLowerCase();

  if (name === 'paid leave' || name === 'unpaid leave') return 0.5;
  if (name === 'late arrival' || name === 'early exit') return 1;
  return 1;
}

export function formatViolationLeaveDeductionLabel(row) {
  const days = getViolationLeaveDeductionDays(row);
  return `${days} day${days === 1 ? '' : 's'}`;
}

export function getViolationLeaveTypeDisplayName(row) {
  return String(row?.name || '').trim() || '—';
}

export function isViolationLeaveTypeSelectable(row) {
  if (isUnpaidLeaveType(row)) return true;
  const balance = getViolationLeaveTypeBalance(row);
  return balance >= getViolationLeaveDeductionDays(row);
}

export function formatViolationLeaveTypeListLabel(row) {
  const name = getViolationLeaveTypeDisplayName(row);
  if (isUnpaidLeaveType(row)) return name;

  const balance = getViolationLeaveTypeBalance(row);
  return `${name} — ${balance} day${balance === 1 ? '' : 's'}`;
}

/** Leave types allowed in the violation apply-leave modal. */
export function filterViolationApplyLeaveTypes(leaveTypes, violationType) {
  const violationLeaveName = getLeaveTypeName(violationType).trim().toLowerCase();
  const allowed = new Set([
    ...VIOLATION_APPLY_LEAVE_BASE_NAMES,
    ...(violationLeaveName ? [violationLeaveName] : [])
  ]);

  const filtered = (Array.isArray(leaveTypes) ? leaveTypes : []).filter((row) => {
    const name = String(row?.name || '')
      .trim()
      .toLowerCase();
    return allowed.has(name);
  });

  const sortOrder = [
    ...(violationLeaveName ? [violationLeaveName] : []),
    ...VIOLATION_APPLY_LEAVE_BASE_NAMES
  ];

  return filtered.sort((a, b) => {
    const ai = sortOrder.indexOf(
      String(a?.name || '')
        .trim()
        .toLowerCase()
    );
    const bi = sortOrder.indexOf(
      String(b?.name || '')
        .trim()
        .toLowerCase()
    );
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
}

export function getWarningStatusConfig(status) {
  return WARNING_STATUS_CONFIG[status] || { label: status || '—', color: 'default' };
}

export function getViolationEmployeeName(row) {
  const user = row?.user;
  if (user && typeof user === 'object') {
    return (
      user?.name ||
      user?.userName ||
      `${user?.firstName || ''} ${user?.lastName || ''}`.trim() ||
      '—'
    );
  }
  return row?.userName || row?.employeeName || '—';
}

export function getViolationEmployeeSubtext(row) {
  const user = row?.user;
  if (user && typeof user === 'object') {
    return user?.designation || user?.email || '—';
  }
  return row?.userEmail || row?.email || '—';
}

export function getViolationEmployeeEmail(row) {
  return getViolationEmployeeSubtext(row);
}

export function getViolationUserId(row) {
  if (row?.userId && typeof row.userId === 'string') return row.userId;
  const user = row?.user;
  if (user && typeof user === 'object') return user?._id || user?.id || null;
  return null;
}

export function getViolationRowId(row) {
  return getViolationUserId(row) || `user-${row?.user?.userName || 'unknown'}`;
}

export function getSummaryRowId(userRow, summary) {
  return `${getViolationRowId(userRow)}-${summary?.violationType || 'summary'}`;
}

export function normalizeMyWarningsResponse(response) {
  let payload = response?.data ?? response ?? {};

  if (payload?.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
    const inner = payload.data;
    if (
      inner.summaries ||
      inner.violationSummaries ||
      inner.totalWarnings != null ||
      inner.totalViolationsThisMonth != null ||
      inner.month != null
    ) {
      payload = inner;
    }
  }

  const summaries = Array.isArray(payload?.summaries)
    ? payload.summaries
    : Array.isArray(payload?.violationSummaries)
      ? payload.violationSummaries
      : [];

  const totalWarnings =
    Number(payload?.totalWarnings) ||
    Number(payload?.totalViolationsThisMonth) ||
    summaries.reduce((sum, summary) => {
      const count = Number(summary?.monthlyViolationCount);
      if (count > 0) return sum + count;
      return sum + (Array.isArray(summary?.violations) ? summary.violations.length : 0);
    }, 0) ||
    0;

  return {
    userId: payload?.userId || null,
    month: Number(payload?.month) || null,
    year: Number(payload?.year) || null,
    totalWarnings,
    summaries
  };
}

export function normalizeMonthlyViolationsResponse(response) {
  const payload = response?.data;

  if (Array.isArray(payload)) {
    return {
      items: payload,
      totalCount: payload.length,
      skip: 0,
      limit: payload.length,
      isNext: false
    };
  }

  const items = Array.isArray(payload?.items) ? payload.items : [];
  const totalCount = Number(payload?.totalCount) || items.length;
  const skip = Number(payload?.skip) || 0;
  const limit = Number(payload?.limit) || items.length;

  return {
    items,
    totalCount,
    skip,
    limit,
    isNext: skip + items.length < totalCount
  };
}

export function isViolationLeaveApplied(summary) {
  if (summary?.leaveApplied || summary?.warningStatus === 'LEAVE_APPLIED') return true;

  const pendingId = summary?.pendingViolationId;
  if (!pendingId || !Array.isArray(summary?.violations)) return false;

  return summary.violations.some(
    (violation) => violation?.violationId === pendingId && violation?.leaveApplied
  );
}

export function canShowApplyLeave(summary) {
  if (isViolationLeaveApplied(summary)) return false;
  return Boolean(summary?.showApplyLeave);
}

export function userNeedsHrAction(userRow) {
  if (userRow?.showApplyLeave || userRow?.warningStatus === 'HR_NOTIFIED') return true;
  return getViolationSummaries(userRow).some(
    (summary) => summary?.showApplyLeave || summary?.warningStatus === 'HR_NOTIFIED'
  );
}

export const VIOLATION_PERIOD_TYPES = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  CUSTOM: 'custom'
};

export const VIOLATION_PERIOD_OPTIONS = [
  { value: VIOLATION_PERIOD_TYPES.DAY, label: 'Day' },
  { value: VIOLATION_PERIOD_TYPES.WEEK, label: 'Week' },
  { value: VIOLATION_PERIOD_TYPES.MONTH, label: 'Month' },
  { value: VIOLATION_PERIOD_TYPES.CUSTOM, label: 'Custom' }
];

function toViolationMoment(input) {
  if (!input) return null;
  const m = moment(input, ['YYYY-MM-DD', 'YYYYMMDD'], true);
  return m.isValid() ? m : null;
}

export function toViolationDateParam(input) {
  const m = toViolationMoment(input);
  return m ? m.format('YYYYMMDD') : null;
}

export function getViolationWeekRange(dateInput) {
  const anchor = toViolationMoment(dateInput) || moment();
  return {
    start: anchor.clone().startOf('week'),
    end: anchor.clone().endOf('week')
  };
}

export function buildViolationFilterParams(state = {}) {
  const periodType = state.periodType || VIOLATION_PERIOD_TYPES.MONTH;
  const params = {
    violationType: state.violationType === 'all' ? undefined : state.violationType,
    search: state.search?.trim() || undefined
  };

  if (periodType === VIOLATION_PERIOD_TYPES.DAY) {
    const date = toViolationDateParam(state.date);
    if (date) params.date = date;
    return params;
  }

  if (periodType === VIOLATION_PERIOD_TYPES.WEEK) {
    const { start, end } = getViolationWeekRange(state.date);
    params.startDate = start.format('YYYYMMDD');
    params.endDate = end.format('YYYYMMDD');
    return params;
  }

  if (periodType === VIOLATION_PERIOD_TYPES.CUSTOM) {
    const startDate = toViolationDateParam(state.startDate);
    const endDate = toViolationDateParam(state.endDate);
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return params;
  }

  params.month = Number(state.month) || moment().month() + 1;
  params.year = Number(state.year) || moment().year();
  return params;
}

export function getViolationPeriodLabel(state = {}) {
  const periodType = state.periodType || VIOLATION_PERIOD_TYPES.MONTH;

  if (periodType === VIOLATION_PERIOD_TYPES.DAY) {
    const m = toViolationMoment(state.date);
    return m ? m.format('DD MMM YYYY') : '—';
  }

  if (periodType === VIOLATION_PERIOD_TYPES.WEEK) {
    const { start, end } = getViolationWeekRange(state.date);
    return `${start.format('DD MMM')} – ${end.format('DD MMM YYYY')}`;
  }

  if (periodType === VIOLATION_PERIOD_TYPES.CUSTOM) {
    const start = toViolationMoment(state.startDate);
    const end = toViolationMoment(state.endDate);
    if (start && end) return `${start.format('DD MMM YYYY')} – ${end.format('DD MMM YYYY')}`;
    if (start) return `From ${start.format('DD MMM YYYY')}`;
    if (end) return `Until ${end.format('DD MMM YYYY')}`;
    return '—';
  }

  const month = Number(state.month) || moment().month() + 1;
  const year = Number(state.year) || moment().year();
  return new Date(year, month - 1, 1).toLocaleString(undefined, {
    month: 'long',
    year: 'numeric'
  });
}

export function buildViolationApplyLeavePayload(violation, leaveTypeId) {
  return {
    violationId: getViolationId(violation),
    leaveTypeId
  };
}

export function getViolationId(violation = {}) {
  return violation?.violationId || violation?.pendingViolationId || null;
}

export function isViolationDismissable(summary, violation) {
  return canApplyLeaveForViolation(summary, violation) && Boolean(getViolationId(violation));
}

export function getDismissableViolationIds(summary) {
  return getSortedViolations(summary)
    .filter((violation) => isViolationDismissable(summary, violation))
    .map((violation) => getViolationId(violation));
}
